import axios, { AxiosError, AxiosResponseHeaders } from 'axios';
import type { CartMigrationDetail } from 'domains/cart';

import { API_ROOT_URL, API_TOKEN } from 'utils/globals';

const headers = {
  common: {
    Authorization: `Token token="${API_TOKEN}"`,
  },
};

const CART_GUID_HEADER = 'X-Cart-Guid';
const MIGRATION_HEADER = 'X-Cart-Requires-Migration';

const api = axios.create({ baseURL: `${API_ROOT_URL}/api/cart/v1`, headers });

export default api;

export function setCartGuidHeader(guid: axios.AxiosHeaderValue) {
  api.defaults.headers[CART_GUID_HEADER] = guid;
}

export function getCartGuidHeader() {
  return api.defaults.headers[CART_GUID_HEADER];
}

export function mustForceMigrationRefresh(migration: CartMigrationDetail) {
  return (
    migration.removed_cart_order_option_ids.length > 0 ||
    migration.removed_cart_product_ids.length > 0 ||
    !!migration.removed_cart_shipping_method_id
  );
}

export function forceMigrationRefresh() {
  window.alert('We have detected outdated items in your cart. Your page will refresh.');
  window.location.reload();
}
/**
 * All requests through the cart API might respond with an error & header value indicating
 * that the cart was on an outdated catalog snapshot. If this happens, we need to attempt
 * to migrate the cart to the newest snapshot. In the case that this migration goes well
 * and no products/options have changed, we can just retry the user's original request and
 * continue on. If something DID change (which in the timeline of a session is very unlikely),
 * we have no option but to stop what was happening, alert the user, and force a page refresh.
 */
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const headers = error.response?.headers;
    function isAxiosHeaders(h: typeof headers): h is AxiosResponseHeaders {
      return !!h && !!h.has;
    }

    if (
      error.response?.status === 409 &&
      isAxiosHeaders(headers) &&
      headers.has(MIGRATION_HEADER)
    ) {
      const original = error.config;
      if (!original) return Promise.reject(error);

      try {
        const { data } = await api.post<CartMigrationDetail>('/migrate');

        if (mustForceMigrationRefresh(data)) {
          forceMigrationRefresh();
          return;
        } else {
          return axios(original);
        }
      } catch (secondError) {
        return Promise.reject(secondError);
      }
    }

    return Promise.reject(error);
  }
);
